import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { TabPanel, Tab, TabPanelAlignment } from 'src/components/controls/Tab';
import { ODataOptions } from 'src/util/ODataBuilder';
import { useInfiniteScroll, IPagedResult } from 'src/util/useInfiniteScroll';
import { useFillRemainingHeight } from 'src/util/useFillRemainingHeight';
import ICostBreakdownProject from '../models/costBreakdownProject';
import ICostBreakdownSection from '../models/costBreakdownSection';
import ICostBreakdownUnit from '../models/costBreakdownUnit';
import ICostBreakdownAccount from '../models/costBreakdownAccount';
import ICostBreakdownPersonAccount from '../models/costBreakdownPersonAccount';
import ICostBreakdownDetailRow from '../models/costBreakdownDetailRow';
import ICostBreakdownTotals from '../models/costBreakdownTotals';
import './CostBreakdownQueryView.scss';

interface ICostBreakdownQueryViewProps {
    rptId: number;
    onBack: () => void;
}

const PAGE_SIZE = 50;

// How much space to reserve below the grid: enough for the totals footer table plus a little
// bottom margin (tabs with a footer), or just the margin (Detail, which has none).
const FOOTER_RESERVE_PX = 72;
const NO_FOOTER_RESERVE_PX = 32;

const formatDate = (value: string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
};

const formatMoney = (value?: number): string =>
    (value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatNumber = (value?: number): string =>
    (value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Column widths (%) shared by a tab's header/body table and its totals-footer table - both tables
// use the same colgroup + table-layout:fixed so the footer's cells line up exactly under the
// matching header columns even though they're two separate <table> elements.
const PROJECTS_WIDTHS = [6, 6, 24, 9, 10, 10, 11, 10, 8, 6];
const SECTIONS_WIDTHS = [5, 5, 18, 5, 15, 14, 8, 8, 8, 8, 6];
const UNITS_WIDTHS = [4, 4, 14, 4, 11, 4, 11, 11, 7, 7, 7, 7, 9];
const ACCOUNTS_WIDTHS = [4, 4, 12, 4, 9, 4, 9, 4, 9, 9, 6, 6, 6, 6, 8];
const PERSON_ACCOUNTS_WIDTHS = [4, 4, 16, 4, 14, 5, 14, 7, 8, 8, 8, 8];

const colGroup = (widths: Array<number>) => (
    <colgroup>
        {widths.map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}
    </colgroup>
);

// Renders the always-visible totals row as its own table, positioned as a sibling directly below
// the scrolling grid div (not inside it, and not via CSS position:sticky - both were unreliable
// for this in practice) so it can never be scrolled out of view.
const renderFooter = (widths: Array<number>, labelSpan: number, totals: ICostBreakdownTotals | undefined, cells: Array<(t: ICostBreakdownTotals) => React.ReactNode>) => (
    <table style={{ width: '100%', tableLayout: 'fixed' }}>
        {colGroup(widths)}
        <tbody>
            <tr style={{ fontWeight: 'bold', borderTop: '2px solid #999' }}>
                <td colSpan={labelSpan}>Totals</td>
                {cells.map((fn, i) => <td key={i}>{totals ? fn(totals) : ''}</td>)}
            </tr>
        </tbody>
    </table>
);

// Mirrors the TABCONTROL canvas's own 6 tab pages (same order Oracle uses), each backed directly by
// one of the pre-built COST_BREAKDOWN_* views - no client-side aggregation, the server just selects
// from the view filtered by rpt_id. Each tab loads more rows as the user scrolls to the bottom of
// its own grid (useInfiniteScroll), rather than fetching everything up front - Detail especially can
// run into the thousands of rows for a large report. Totals are summed server-side across the whole
// report (independent of paging) and fetched once per tab, alongside that tab's first page load.
const CostBreakdownQueryView = (props: ICostBreakdownQueryViewProps) => {
    const { get, response } = useFetch();
    const { get: getTotals } = useFetch();
    const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());
    const [totals, setTotals] = useState<Record<string, ICostBreakdownTotals | undefined>>({});
    // Only one <Tab>'s content is ever mounted at a time (TabPanel renders just the current tab),
    // so these two refs are safely shared across all 6 tab bodies - whichever one is showing
    // attaches to the matching ref.
    const gridWithFooter = useFillRemainingHeight<HTMLDivElement>(FOOTER_RESERVE_PX);
    const gridNoFooter = useFillRemainingHeight<HTMLDivElement>(NO_FOOTER_RESERVE_PX);

    const fetchPage = <T, >(path: string) => async (pageNumber: number): Promise<IPagedResult<T>> => {
        const options = new ODataOptions().addPaging(pageNumber, PAGE_SIZE);
        const result = await get(`/v1.0/CostBreakdown/reports/${props.rptId}/${path}${options.toString()}`);
        if (!response.ok) {
            return { items: [], totalRecords: 0 };
        }
        return { items: result?.items ?? [], totalRecords: result?.totalRecords ?? 0 };
    };

    const projects = useInfiniteScroll<ICostBreakdownProject>(fetchPage<ICostBreakdownProject>('projects'), PAGE_SIZE);
    const sections = useInfiniteScroll<ICostBreakdownSection>(fetchPage<ICostBreakdownSection>('sections'), PAGE_SIZE);
    const units = useInfiniteScroll<ICostBreakdownUnit>(fetchPage<ICostBreakdownUnit>('units'), PAGE_SIZE);
    const accounts = useInfiniteScroll<ICostBreakdownAccount>(fetchPage<ICostBreakdownAccount>('accounts'), PAGE_SIZE);
    const personAccounts = useInfiniteScroll<ICostBreakdownPersonAccount>(fetchPage<ICostBreakdownPersonAccount>('person-accounts'), PAGE_SIZE);
    const detail = useInfiniteScroll<ICostBreakdownDetailRow>(fetchPage<ICostBreakdownDetailRow>('detail'), PAGE_SIZE);

    const tabScrolls: Record<string, { loadNextPage: () => Promise<void> }> = {
        Projects: projects,
        Subcode: sections,
        'Alt Subcode': units,
        Accounts: accounts,
        'Personnel/Accounts': personAccounts,
        Detail: detail,
    };

    // Detail has no totals row in Oracle's own screen, so it's left out of this map.
    const totalsPaths: Record<string, string> = {
        Projects: 'projects',
        Subcode: 'sections',
        'Alt Subcode': 'units',
        Accounts: 'accounts',
        'Personnel/Accounts': 'person-accounts',
    };

    const fetchTotalsForTab = async (tabName: string) => {
        const path = totalsPaths[tabName];
        if (!path) {
            return;
        }
        const result = await getTotals(`/v1.0/CostBreakdown/reports/${props.rptId}/${path}/totals`);
        setTotals((prev) => ({ ...prev, [tabName]: result }));
    };

    useEffect(() => {
        projects.reset();
        sections.reset();
        units.reset();
        accounts.reset();
        personAccounts.reset();
        detail.reset();
        setTotals({});
        setLoadedTabs(new Set(['Projects']));
        projects.loadNextPage();
        fetchTotalsForTab('Projects');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.rptId]);

    const handleTabChange = (tabName: string) => {
        if (loadedTabs.has(tabName)) {
            return;
        }
        setLoadedTabs((prev) => new Set(prev).add(tabName));
        tabScrolls[tabName]?.loadNextPage();
        fetchTotalsForTab(tabName);
    };

    const loadingRow = (colSpan: number) => (
        <tr>
            <td colSpan={colSpan} className="bold-centered">
                <LoadingSpinner />
            </td>
        </tr>
    );

    const emptyRow = (colSpan: number) => (
        <tr>
            <td colSpan={colSpan} className="bold-centered">No Data</td>
        </tr>
    );

    return (
        <div className="cost-breakdown-tab-panel">
            <div style={{ marginBottom: '.5rem' }}>
                <Button
                    text="Show Reports"
                    style={{ background: 'none', border: 'none', padding: 0, color: '#6393C1', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => props.onBack()}
                />
            </div>
            <TabPanel alignment={TabPanelAlignment.Horizontal} onChange={(tabName) => handleTabChange(tabName)}>
                <Tab tabName="Projects">
                    <div ref={gridWithFooter.ref} style={{ overflowY: 'scroll', height: gridWithFooter.height }} onScroll={projects.onScroll}>
                        <table style={{ width: '100%', tableLayout: 'fixed' }}>
                            {colGroup(PROJECTS_WIDTHS)}
                            <thead>
                                <tr>
                                    <th>Cl. No.</th><th>Pr. No.</th><th style={{ textAlign: 'left' }}>Project</th>
                                    <th>Total Days</th><th>Services</th><th>Expenses</th><th>Office Expenses</th>
                                    <th>Total</th><th>Budget</th><th>Pct</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projects.loading && projects.items.length === 0 && loadingRow(10)}
                                {!projects.loading && projects.items.length === 0 && emptyRow(10)}
                                {projects.items.map((row, index) => (
                                    <tr key={`${row.clNo}-${row.prNo}-${index}`}>
                                        <td>{row.clNo}</td><td>{row.prNo}</td><td style={{ textAlign: 'left' }}>{row.prName}</td>
                                        <td>{formatNumber(row.totalDays)}</td><td>{formatMoney(row.lmServices)}</td>
                                        <td>{formatMoney(row.lmExpenses)}</td><td>{formatMoney(row.officeExpenses)}</td>
                                        <td>{formatMoney(row.total)}</td><td>{formatMoney(row.budgetAmount)}</td>
                                        <td>{formatNumber(row.pct)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {renderFooter(PROJECTS_WIDTHS, 3, totals.Projects, [
                        (t) => formatNumber(t.totalDays),
                        (t) => formatMoney(t.lmServices),
                        (t) => formatMoney(t.lmExpenses),
                        (t) => formatMoney(t.officeExpenses),
                        (t) => formatMoney(t.total),
                        (t) => formatMoney(t.budgetAmount),
                        () => '',
                    ])}
                </Tab>
                <Tab tabName="Subcode">
                    <div ref={gridWithFooter.ref} style={{ overflowY: 'scroll', height: gridWithFooter.height }} onScroll={sections.onScroll}>
                        <table style={{ width: '100%', tableLayout: 'fixed' }}>
                            {colGroup(SECTIONS_WIDTHS)}
                            <thead>
                                <tr>
                                    <th>Cl. No.</th><th>Pr. No.</th><th style={{ textAlign: 'left' }}>Project</th>
                                    <th>Sc. No.</th><th style={{ textAlign: 'left' }}>Section/Survey</th>
                                    <th style={{ textAlign: 'left' }}>Client Landman</th>
                                    <th>Total Days</th><th>Services</th><th>Expenses</th><th>Office Expenses</th><th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sections.loading && sections.items.length === 0 && loadingRow(11)}
                                {!sections.loading && sections.items.length === 0 && emptyRow(11)}
                                {sections.items.map((row, index) => (
                                    <tr key={`${row.clNo}-${row.prNo}-${row.scNo}-${index}`}>
                                        <td>{row.clNo}</td><td>{row.prNo}</td><td style={{ textAlign: 'left' }}>{row.prName}</td>
                                        <td>{row.scNo}</td><td style={{ textAlign: 'left' }}>{row.str}</td>
                                        <td style={{ textAlign: 'left' }}>{row.clientLandman}</td>
                                        <td>{formatNumber(row.totalDays)}</td><td>{formatMoney(row.lmServices)}</td>
                                        <td>{formatMoney(row.lmExpenses)}</td><td>{formatMoney(row.officeExpenses)}</td>
                                        <td>{formatMoney(row.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {renderFooter(SECTIONS_WIDTHS, 6, totals.Subcode, [
                        (t) => formatNumber(t.totalDays),
                        (t) => formatMoney(t.lmServices),
                        (t) => formatMoney(t.lmExpenses),
                        (t) => formatMoney(t.officeExpenses),
                        (t) => formatMoney(t.total),
                    ])}
                </Tab>
                <Tab tabName="Alt Subcode">
                    <div ref={gridWithFooter.ref} style={{ overflowY: 'scroll', height: gridWithFooter.height }} onScroll={units.onScroll}>
                        <table style={{ width: '100%', tableLayout: 'fixed' }}>
                            {colGroup(UNITS_WIDTHS)}
                            <thead>
                                <tr>
                                    <th>Cl. No.</th><th>Pr. No.</th><th style={{ textAlign: 'left' }}>Project</th>
                                    <th>Sc. No.</th><th style={{ textAlign: 'left' }}>Section/Survey</th>
                                    <th>Un. No.</th><th style={{ textAlign: 'left' }}>Unit</th>
                                    <th style={{ textAlign: 'left' }}>Client Landman</th>
                                    <th>Total Days</th><th>Services</th><th>Expenses</th><th>Off. Exp.</th><th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {units.loading && units.items.length === 0 && loadingRow(13)}
                                {!units.loading && units.items.length === 0 && emptyRow(13)}
                                {units.items.map((row, index) => (
                                    <tr key={`${row.clNo}-${row.prNo}-${row.scNo}-${row.unNo}-${index}`}>
                                        <td>{row.clNo}</td><td>{row.prNo}</td><td style={{ textAlign: 'left' }}>{row.prName}</td>
                                        <td>{row.scNo}</td><td style={{ textAlign: 'left' }}>{row.str}</td>
                                        <td>{row.unNo}</td><td style={{ textAlign: 'left' }}>{row.unitName}</td>
                                        <td style={{ textAlign: 'left' }}>{row.clientLandman}</td>
                                        <td>{formatNumber(row.totalDays)}</td><td>{formatMoney(row.lmServices)}</td>
                                        <td>{formatMoney(row.lmExpenses)}</td><td>{formatMoney(row.officeExpenses)}</td>
                                        <td>{formatMoney(row.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {renderFooter(UNITS_WIDTHS, 8, totals['Alt Subcode'], [
                        (t) => formatNumber(t.totalDays),
                        (t) => formatMoney(t.lmServices),
                        (t) => formatMoney(t.lmExpenses),
                        (t) => formatMoney(t.officeExpenses),
                        (t) => formatMoney(t.total),
                    ])}
                </Tab>
                <Tab tabName="Accounts">
                    <div ref={gridWithFooter.ref} style={{ overflowY: 'scroll', height: gridWithFooter.height }} onScroll={accounts.onScroll}>
                        <table style={{ width: '100%', tableLayout: 'fixed' }}>
                            {colGroup(ACCOUNTS_WIDTHS)}
                            <thead>
                                <tr>
                                    <th>Cl. No.</th><th>Pr. No.</th><th style={{ textAlign: 'left' }}>Project</th>
                                    <th>Sc. No.</th><th style={{ textAlign: 'left' }}>Section/Survey</th>
                                    <th>Un. No.</th><th style={{ textAlign: 'left' }}>Unit</th>
                                    <th>Acct No.</th><th style={{ textAlign: 'left' }}>Accounts</th>
                                    <th style={{ textAlign: 'left' }}>Cl. Landman</th>
                                    <th>Total Days</th><th>Services</th><th>Expenses</th><th>Off. Exp.</th><th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.loading && accounts.items.length === 0 && loadingRow(15)}
                                {!accounts.loading && accounts.items.length === 0 && emptyRow(15)}
                                {accounts.items.map((row, index) => (
                                    <tr key={`${row.clNo}-${row.prNo}-${row.scNo}-${row.unNo}-${row.acctNo}-${index}`}>
                                        <td>{row.clNo}</td><td>{row.prNo}</td><td style={{ textAlign: 'left' }}>{row.prName}</td>
                                        <td>{row.scNo}</td><td style={{ textAlign: 'left' }}>{row.str}</td>
                                        <td>{row.unNo}</td><td style={{ textAlign: 'left' }}>{row.unitName}</td>
                                        <td>{row.acctNo}</td><td style={{ textAlign: 'left' }}>{row.acctType}</td>
                                        <td style={{ textAlign: 'left' }}>{row.clientLandman}</td>
                                        <td>{formatNumber(row.totalDays)}</td><td>{formatMoney(row.lmServices)}</td>
                                        <td>{formatMoney(row.lmExpenses)}</td><td>{formatMoney(row.officeExpenses)}</td>
                                        <td>{formatMoney(row.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {renderFooter(ACCOUNTS_WIDTHS, 10, totals.Accounts, [
                        (t) => formatNumber(t.totalDays),
                        (t) => formatMoney(t.lmServices),
                        (t) => formatMoney(t.lmExpenses),
                        (t) => formatMoney(t.officeExpenses),
                        (t) => formatMoney(t.total),
                    ])}
                </Tab>
                <Tab tabName="Personnel/Accounts">
                    <div ref={gridWithFooter.ref} style={{ overflowY: 'scroll', height: gridWithFooter.height }} onScroll={personAccounts.onScroll}>
                        <table style={{ width: '100%', tableLayout: 'fixed' }}>
                            {colGroup(PERSON_ACCOUNTS_WIDTHS)}
                            <thead>
                                <tr>
                                    <th>Cl. No.</th><th>Pr. No.</th><th style={{ textAlign: 'left' }}>Project</th>
                                    <th>No.</th><th style={{ textAlign: 'left' }}>Name</th>
                                    <th>Acct No.</th><th style={{ textAlign: 'left' }}>Accounts</th>
                                    <th>Total Days</th><th>Services</th><th>Expenses</th><th>Off. Exp.</th><th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {personAccounts.loading && personAccounts.items.length === 0 && loadingRow(12)}
                                {!personAccounts.loading && personAccounts.items.length === 0 && emptyRow(12)}
                                {personAccounts.items.map((row, index) => (
                                    <tr key={`${row.clNo}-${row.prNo}-${row.tsdNo}-${row.acctNo}-${index}`}>
                                        <td>{row.clNo}</td><td>{row.prNo}</td><td style={{ textAlign: 'left' }}>{row.prName}</td>
                                        <td>{row.tsdNo}</td><td style={{ textAlign: 'left' }}>{row.name}</td>
                                        <td>{row.acctNo}</td><td style={{ textAlign: 'left' }}>{row.acctType}</td>
                                        <td>{formatNumber(row.totalDays)}</td><td>{formatMoney(row.lmServices)}</td>
                                        <td>{formatMoney(row.lmExpenses)}</td><td>{formatMoney(row.officeExpenses)}</td>
                                        <td>{formatMoney(row.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {renderFooter(PERSON_ACCOUNTS_WIDTHS, 7, totals['Personnel/Accounts'], [
                        (t) => formatNumber(t.totalDays),
                        (t) => formatMoney(t.lmServices),
                        (t) => formatMoney(t.lmExpenses),
                        (t) => formatMoney(t.officeExpenses),
                        (t) => formatMoney(t.total),
                    ])}
                </Tab>
                <Tab tabName="Detail">
                    <div ref={gridNoFooter.ref} style={{ overflowY: 'scroll', height: gridNoFooter.height }} onScroll={detail.onScroll}>
                        <table style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Cl. No.</th><th>Pr. No.</th><th style={{ textAlign: 'left' }}>Project</th>
                                    <th>No.</th><th style={{ textAlign: 'left' }}>Name</th>
                                    <th>Sc. No.</th><th>Un. No.</th>
                                    <th>Acct No.</th><th style={{ textAlign: 'left' }}>Accounts</th>
                                    <th>Work Date</th><th>Total Days</th><th>Services</th><th>Expenses</th><th>Total</th>
                                    <th style={{ textAlign: 'left' }}>Work Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.loading && detail.items.length === 0 && loadingRow(15)}
                                {!detail.loading && detail.items.length === 0 && emptyRow(15)}
                                {detail.items.map((row, index) => (
                                    <tr key={`${row.clNo}-${row.prNo}-${row.tsdNo}-${row.workDate}-${index}`}>
                                        <td>{row.clNo}</td><td>{row.prNo}</td><td style={{ textAlign: 'left' }}>{row.prName}</td>
                                        <td>{row.tsdNo}</td><td style={{ textAlign: 'left' }}>{row.name}</td>
                                        <td>{row.scNo}</td><td>{row.unNo}</td>
                                        <td>{row.acctNo}</td><td style={{ textAlign: 'left' }}>{row.acctType}</td>
                                        <td>{formatDate(row.workDate)}</td><td>{formatNumber(row.totalDays)}</td>
                                        <td>{formatMoney(row.lmServices)}</td><td>{formatMoney(row.lmExpenses)}</td>
                                        <td>{formatMoney(row.total)}</td><td style={{ textAlign: 'left' }}>{row.dscr}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Tab>
            </TabPanel>
        </div>
    );
};

export default CostBreakdownQueryView;



// WEBPACK FOOTER //
// ./src/components/project-billing/components/CostBreakdownQueryView.tsx